/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShieldCheck, X, LockKeyhole, Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { createPortal } from "react-dom";
import { markSensitiveActionVerified } from "../../utils/sensitiveActionAuth";

export default function SensitiveActionModal({
  open,
  onClose,
  onVerified,
  title = "Confirm sensitive action",
  description = "For security, enter your authenticator code before continuing.",
  actionLabel = "Continue securely",
  closeAfterVerified = true,
}) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const inputsRef = useRef([]);

  const code = useMemo(() => digits.join(""), [digits]);

  useEffect(() => {
    if (!open) return undefined;

    setDigits(["", "", "", "", "", ""]);
    setRecoveryCode("");
    setUseRecovery(false);
    setBusy(false);
    setError("");

    const focusTimer = window.setTimeout(() => {
      inputsRef.current?.[0]?.focus?.({ preventScroll: true });
    }, 80);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  if (!open) return null;

  function handleDigitChange(index, value) {
    const clean = value.replace(/\D/g, "").slice(-1);

    setDigits((current) => {
      const next = [...current];
      next[index] = clean;
      return next;
    });

    if (clean && index < 5) {
      inputsRef.current[index + 1]?.focus?.();
    }
  }

  function handleKeyDown(index, event) {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus?.();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (busy) return;

    setError("");

    const value = useRecovery
      ? recoveryCode.trim()
      : code.trim();

    if (!value || (!useRecovery && value.length !== 6)) {
      setError(
        useRecovery
          ? "Enter a valid recovery code."
          : "Enter the latest 6-digit authenticator code."
      );
      return;
    }

    setBusy(true);

    try {
      const callable = httpsCallable(
        functions,
        useRecovery ? "verifyRecoveryCode" : "verifyAdminMfa"
      );

const result = await callable(
  useRecovery ? { recoveryCode: value } : { token: value }
);

if (!result?.data?.success && !result?.data?.ok) {
  throw new Error("Verification failed.");
}

      markSensitiveActionVerified();

      if (typeof onVerified === "function") {
        await onVerified();
      }

      if (closeAfterVerified && typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      console.error("Sensitive action verification failed:", err);
      setError(
        err?.message ||
          "Verification failed. Please use the latest code and try again."
      );
    } finally {
      setBusy(false);
    }
  }

return createPortal(
  <div style={styles.backdrop} role="presentation">
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sensitive-action-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" style={styles.closeBtn} onClick={onClose}>
          <X size={18} />
        </button>

        <div style={styles.iconWrap}>
          <ShieldCheck size={32} />
        </div>

        <p style={styles.eyebrow}>Sensitive action</p>
        <h2 id="sensitive-action-title" style={styles.title}>{title}</h2>
        <p style={styles.description}>{description}</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {!useRecovery ? (
            <div style={styles.codeGrid}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputsRef.current[index] = el;
                  }}
                  value={digit}
                  onChange={(e) =>
                    handleDigitChange(index, e.target.value)
                  }
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  inputMode="numeric"
                  maxLength={1}
                  style={styles.codeInput}
                  disabled={busy}
                />
              ))}
            </div>
          ) : (
            <input
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              placeholder="XXXXXXXX-XXXXXXXX"
              style={styles.recoveryInput}
              disabled={busy}
            />
          )}

          {error ? <div style={styles.error}>{error}</div> : null}

          <button type="submit" style={styles.primaryBtn} disabled={busy}>
            {busy ? <Loader2 size={18} /> : <LockKeyhole size={18} />}
            {busy ? "Verifying..." : actionLabel}
          </button>

          <button
            type="button"
            style={styles.linkBtn}
            onClick={() => {
              setError("");
              setUseRecovery((v) => !v);
            }}
            disabled={busy}
          >
            {useRecovery
              ? "Use authenticator code instead"
              : "Use recovery code instead"}
          </button>
        </form>
      </div>
    </div>
  ,
  document.body
);
}

const styles = {
backdrop: {
  position: "fixed",
  inset: 0,
  zIndex: 999999,
  background: "rgba(15, 23, 42, 0.58)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 18,
  overscrollBehavior: "contain",
  isolation: "isolate",
},

modal: {
  width: "min(620px, calc(100vw - 32px))",
  position: "relative",
  borderRadius: 30,
  background: "#fff",
  border: "1px solid #e5ebf7",
  boxShadow: "0 30px 90px rgba(15,23,42,0.28)",
  padding: 32,
  textAlign: "center",
  overflow: "hidden",
  boxSizing: "border-box",
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
  contain: "layout paint",
},

form: {
  display: "grid",
  gap: 14,
  marginTop: 22,
  paddingTop: 18,
  borderTop: "1px solid #e5ebf7",
  width: "100%",
  boxSizing: "border-box",
},

codeGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(6, 56px)",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  overflow: "hidden",
},

codeInput: {
  width: 56,
  height: 62,
  borderRadius: 18,
  border: "1px solid #dbe7ff",
  background: "#fff",
  textAlign: "center",
  fontSize: 26,
  fontWeight: 950,
  color: "#0f172a",
  outline: "none",
  boxSizing: "border-box",
},

primaryBtn: {
  width: "100%",
  height: 58,
  border: 0,
  borderRadius: 18,
  background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
  color: "#fff",
  fontWeight: 950,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  boxShadow: "0 18px 45px rgba(79,70,229,0.24)",
  boxSizing: "border-box",
},

linkBtn: {
  width: "100%",
  height: 48,
  border: 0,
  borderRadius: 16,
  background: "#f8fafc",
  color: "#4f46e5",
  fontWeight: 900,
  cursor: "pointer",
  boxSizing: "border-box",
},
};